import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useCalculateNegotiationRates,
  useSetupNegotiationAgent,
} from "../../../../services/negotiation/negotiationService";
import classes from "../../TrucksAvailable.module.css";
import { useMixPanelForTracking } from "../../../../utils/TrackMixPanel";
import { UserApiContext } from "../../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../../MyCompanyProfileContext";

const AssignLorriAgentModal = ({
  item,
  onClose,
  company_id,
  fetchSpotData,
  currentFilterPayload,
  defaultRounds,
}) => {
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);
  const truckEnquiryId = item?.truck_enquiry_id;
  const { data, isPending, isError, error, refetch } =
    useCalculateNegotiationRates(truckEnquiryId);
  const {
    mutate: confirmNegotiation,
    isPending: isConfirming,
  } = useSetupNegotiationAgent();
  const [targetRate, setTargetRate] = useState("");
  const [rounds, setRounds] = useState(defaultRounds ?? 3);

  const isCalculated = data?.status === "calculated" || data?.status === "cached";
  const showNoHistory =
    data?.has_history === false || data?.status === "awaiting_manual_input";

  useEffect(() => {
    if (isCalculated && data?.has_history !== false) {
      setTargetRate(data?.target_rate ?? "");
    }
  }, [data]);

  const dedupeLocationName = (name) => {
    if (!name) return name;
    const parts = name.split(",").map((p) => p.trim());
    const seen = [];
    parts.forEach((p) => {
      if (p && !seen.includes(p)) seen.push(p);
    });
    return seen.join(", ");
  };

  const origin = dedupeLocationName(item?.lane_details?.origin?.location_name);
  const destination = dedupeLocationName(item?.lane_details?.destination?.location_name);
  const weight = item?.lane_details?.weight?.value || item?.lane_details?.weight;
  const truckType = Array.isArray(item?.lane_details?.truck_types)
    ? item?.lane_details?.truck_types.map((t) => t?.label).join(", ")
    : item?.lane_details?.truck_types?.label;
  const currentL1 = item?.lane_details?.current_l1_rate || item?.current_l1_rate;

  const httpStatus = isError && error?.response?.status;

  const formatCurrency = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `₹${num.toLocaleString("en-IN")}`;
  };

  const handleConfirmNegotiation = () => {
    if (!Number(targetRate)) {
      toast.error("Please enter a target rate before confirming.");
      return;
    }

    confirmNegotiation(
      [
        {
          truck_enquiry_id: truckEnquiryId,
          agent_enabled: true,
          max_rounds_per_transporter: rounds,
          target_rate: Number(targetRate) || 0,
          walkaway_rate: Number(data?.walkaway_rate) || 0,
          company_id,
        },
      ],
      {
        onSuccess: (res) => {
          const result = Array.isArray(res) ? res[0] : res;
          if (Array.isArray(res) ? result?.status === "ok" : result?.status) {
            toast.success("Negotiation started successfully");
            TrackMixPanel({
              ModuleName: "lorri spot",
              ScreenName: "my enquiries - assign lorri agent",
              Data: `lane: ${origin} to ${destination}, truck type: ${truckType}, target rate: ${targetRate}, rounds: ${rounds}`,
              Definition: `user assigned LoRRI Agent to an enquiry`,
            });
            if (fetchSpotData && currentFilterPayload) {
              fetchSpotData(currentFilterPayload);
            }
            onClose && onClose();
          } else {
            toast.error(result?.error || result?.message || "Something went wrong");
          }
        },
        onError: (err) => {
          toast.error(
            err?.response?.data?.message || "Something went wrong"
          );
        },
      }
    );
  };

  return (
    <div style={{ padding: "8px 4px" }}>
      <div className={classes["lorri-lane-row"]}>
        <div className={classes["lorri-lane-info"]}>
          <div className={classes["lorri-lane-route"]}>
            {origin} → {destination}
          </div>
          <div className={classes["lorri-lane-meta"]}>
            {truckType || ""}
            {weight ? ` (${weight})` : ""}
            {showNoHistory
              ? " · new lane · no past Spot data"
              : currentL1
                ? ` · continues from current L1 ${formatCurrency(currentL1)}`
                : ""}
          </div>
        </div>

        <div className={classes["lorri-lane-target"]}>
          <div className={classes["lorri-lane-label"]}>
            {showNoHistory ? "SET TARGET RATE" : "PROPOSED TARGET"}
          </div>
          {isPending ? (
            <div
              className={classes["skeleton-bar"]}
              style={{ width: "140px", height: "30px", marginTop: "4px" }}
            />
          ) : (
            <input
              type="text"
              inputMode="numeric"
              placeholder={showNoHistory ? "₹—" : ""}
              value={formatCurrency(targetRate)}
              onChange={(e) =>
                setTargetRate(e.target.value.replace(/[^\d]/g, ""))
              }
              className={showNoHistory ? classes["lorri-target-input-empty"] : ""}
              style={{
                border: showNoHistory ? "1px dashed #8a93a0" : "1px solid #cdd3de",
                borderRadius: "6px",
                padding: "6px 10px",
                width: "140px",
                fontWeight: 600,
                color: "#393185",
                marginTop: "4px",
              }}
            />
          )}
        </div>

        <div className={classes["lorri-lane-rounds"]}>
          <div className={classes["lorri-lane-label"]}>
            ROUNDS
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
            <button
              type="button"
              className="btn btn-sm"
              style={{ border: "1px solid #cdd3de" }}
              onClick={() => setRounds((r) => Math.max(1, r - 1))}
            >
              −
            </button>
            <span style={{ fontWeight: 700 }}>{rounds}</span>
            <button
              type="button"
              className="btn btn-sm"
              style={{ border: "1px solid #cdd3de" }}
              onClick={() => setRounds((r) => r + 1)}
            >
              +
            </button>
          </div>
        </div>

        <div className={classes["lorri-lane-status"]}>
          {isPending ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "13px" }}>
              <i className="fa fa-spinner fa-spin" />
              Fetching…
            </div>
          ) : showNoHistory ? (
            <>
              <div className={classes["lorri-badge-info"]}>
                <i className="fa fa-info-circle" /> No historical rates
              </div>
              <div className={classes["lorri-lane-reasoning"]}>
                This lane has no past Spot data to price from. <b>Please enter the target rate</b> you want the agent to work toward.
              </div>
            </>
          ) : isCalculated ? (
            <>
              <div className={classes["lorri-badge-success"]}>
                ✓ Agent's recommendation
              </div>
              <div className={classes["lorri-lane-reasoning"]}>
                Estimated from your historical Spot rates and market intelligence. You can change it to set your own.
              </div>
            </>
          ) : data?.status === "failed" ? (
            <div style={{ color: "#dc2626", fontSize: "13px" }}>
              {data?.message || "Failed to calculate suggestion. You can enter values manually."}
            </div>
          ) : data?.status === "disabled" ? (
            <div style={{ color: "#b45309", fontSize: "13px" }}>
              {data?.reason || "Agent is currently disabled."}
            </div>
          ) : isError ? (
            <div style={{ color: "#dc2626", fontSize: "13px" }}>
              {httpStatus === 400
                ? "Enquiry couldn't be resolved."
                : "Something went wrong, please retry."}
              <button
                type="button"
                className="btn btn-sm btn-link"
                style={{ padding: "0 0 0 6px" }}
                onClick={() => refetch()}
              >
                Retry
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          marginTop: "16px",
          background: "#f3f0fb",
          borderRadius: "8px",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <span style={{ fontWeight: 700, color: "#393185" }}>1 lane ready</span>
          <span style={{ color: "#6b7280", fontSize: "13px", marginLeft: "8px" }}>
            Set the target and rounds for each lane above.
          </span>
        </div>
        <button
          type="button"
          className="btn"
          disabled={data?.status === "disabled" || isConfirming}
          style={{
            background: "#16a34a",
            color: "#fff",
            fontWeight: 600,
            border: "none",
            padding: "8px 16px",
            borderRadius: "6px",
            opacity: isConfirming ? 0.7 : 1,
          }}
          onClick={handleConfirmNegotiation}
        >
          {isConfirming ? "Starting negotiation…" : "✓ Confirm & start negotiation"}
        </button>
      </div>
    </div>
  );
};

export default AssignLorriAgentModal;
